import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, Truck, ShoppingCart, Calendar, TrendingUp, TrendingDown, ArrowRight, MapPin, Filter, User, Search } from 'lucide-react';
import type { DifferencesReportData, TransferDifferenceItem } from '../types/differences.types';
import { DateRangeQuickFilter } from '@/components/ui/date-range-quick-filter';
import { useDateRangeDefaults } from '@/hooks/useDateRangeDefaults';
import { useAccessibleLocations } from '../hooks/useAccessibleLocations';
import { reportsService } from '../services/reportsService';

type DifferencesTab = 'transfers' | 'purchase-orders';

export const DifferencesReport: React.FC = () => {
  const [activeTab, setActiveTab] = useState<DifferencesTab>('transfers');
  const [reportData, setReportData] = useState<DifferencesReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { quickRange, dateFrom, dateTo, setQuickRange, setManualDates } = useDateRangeDefaults('differencesReport');
  const { locationsWithAll, defaultLocationId, loading: locationsLoading } = useAccessibleLocations();

  // Filtros
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [selectedPreparedBy, setSelectedPreparedBy] = useState<string>('all');
  const [preparedByOptions, setPreparedByOptions] = useState<{ id: string; name: string }[]>([
    { id: 'all', name: 'Todos' }
  ]);

  // Auto-seleccionar local cuando se carga
  useEffect(() => {
    if (defaultLocationId) {
      setSelectedLocation(defaultLocationId);
    }
  }, [defaultLocationId]);

  const handleDateChange = (key: string, value: string) => {
    if (key === 'dateFrom') {
      setManualDates(value, dateTo);
    } else if (key === 'dateTo') {
      setManualDates(dateFrom, value);
    }
  };

  const handleFiltersChange = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await reportsService.getDifferencesReport(selectedLocation, {
        startDate: dateFrom,
        endDate: dateTo,
        preparedBy: selectedPreparedBy !== 'all' ? selectedPreparedBy : undefined,
      });
      setReportData(data);

      // Extraer preparadores únicos de las transferencias para el filtro
      if (data?.transfers?.items?.length) {
        const uniquePreparers = new Map<string, string>();
        data.transfers.items.forEach((item: TransferDifferenceItem) => {
          if (item.preparedBy && !uniquePreparers.has(item.preparedBy)) {
            uniquePreparers.set(item.preparedBy, item.preparedBy);
          }
        });
        setPreparedByOptions([
          { id: 'all', name: 'Todos' },
          ...Array.from(uniquePreparers.entries()).map(([id, name]) => ({ id, name }))
        ]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar el reporte');
      console.error('Error loading differences report:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Datos filtrados - ahora los datos ya vienen filtrados del API
  const filteredData = useMemo(() => {
    if (!reportData) return {
      reportDate: new Date().toISOString(),
      transfers: { items: [], totalItems: 0, totalDifferenceValue: 0, averageDifferencePercentage: 0 },
      purchaseOrders: { items: [], totalItems: 0, totalDifferenceValue: 0, averageDifferencePercentage: 0 }
    };
    return reportData;
  }, [reportData]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (percentage: number) => {
    const sign = percentage >= 0 ? '+' : '';
    return `${sign}${percentage.toFixed(1)}%`;
  };

  const getDifferenceColor = (percentage: number) => {
    const absPercentage = Math.abs(percentage);
    if (absPercentage >= 20) return 'text-red-600 bg-red-50';
    if (absPercentage >= 10) return 'text-orange-600 bg-orange-50';
    if (absPercentage >= 5) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const renderTransfersTab = () => (
    <div className="space-y-6">
      {/* Resumen de Transferencias */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Items</CardTitle>
            <Truck className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {filteredData.transfers.totalItems}
            </div>
            <p className="text-xs text-muted-foreground">
              Items con diferencias
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Promedio Diferencia</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {formatPercentage(filteredData.transfers.averageDifferencePercentage)}
            </div>
            <p className="text-xs text-muted-foreground">
              Variación promedio
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabla de Items con Diferencias */}
      <Card>
        <CardHeader>
          <CardTitle>Transferencias con Diferencias</CardTitle>
          <p className="text-sm text-gray-600">Detalle de todos los items con diferencias entre cantidad enviada y confirmada</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <div className="rounded-md border">
              <table className="w-full min-w-max">
              <thead className="bg-gray-50/50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 text-sm">N° Transferencia</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 text-sm">Fecha Creación</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 text-sm">Fecha Recepción</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 text-sm">Origen</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 text-sm">Destino</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 text-sm">Preparado por</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 text-sm">Ingrediente</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-900 text-sm">Cant. Enviada</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-900 text-sm">Cant. Confirmada</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-900 text-sm">Diferencia</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-900 text-sm">% Diferencia</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {filteredData.transfers.items.map((item, index) => (
                  <tr key={item.id} className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">
                      {item.transferNumber}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(item.createdDate).toLocaleDateString('es-PE')}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(item.receivedDate).toLocaleDateString('es-PE')}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {item.fromLocationName}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {item.toLocationName}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3 text-gray-400" />
                        {item.preparedBy}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <div className="font-medium text-gray-900">{item.ingredient.name}</div>
                        <div className="text-xs text-gray-500">{item.ingredient.category} • {item.unit}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">
                      {item.quantitySent}
                    </td>
                    <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">
                      {item.quantityConfirmed}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`text-sm font-medium ${item.difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercentage(0).slice(0, 1)}{item.difference.toFixed(2)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Badge className={getDifferenceColor(item.differencePercentage)}>
                        {formatPercentage(item.differencePercentage)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderPurchaseOrdersTab = () => (
    <div className="space-y-6">
      {/* Resumen de Órdenes de Compra */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Items</CardTitle>
            <ShoppingCart className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {filteredData.purchaseOrders.totalItems}
            </div>
            <p className="text-xs text-muted-foreground">
              Items con diferencias
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Promedio Diferencia</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {formatPercentage(filteredData.purchaseOrders.averageDifferencePercentage)}
            </div>
            <p className="text-xs text-muted-foreground">
              Variación promedio
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabla de Items con Diferencias */}
      <Card>
        <CardHeader>
          <CardTitle>Órdenes de Compra con Diferencias</CardTitle>
          <p className="text-sm text-gray-600">Detalle de todos los items con diferencias entre cantidad recibida y solicitada</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <div className="rounded-md border">
              <table className="w-full min-w-max">
              <thead className="bg-gray-50/50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 text-sm">N° Orden</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 text-sm">Fecha Creación</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 text-sm">Fecha Recepción</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 text-sm">Proveedor</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 text-sm">Destino</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-900 text-sm">Ingrediente</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-900 text-sm">Cant. Solicitada</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-900 text-sm">Cant. Recibida</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-900 text-sm">Diferencia</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-900 text-sm">% Diferencia</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {filteredData.purchaseOrders.items.map((item, index) => (
                  <tr key={item.id} className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                    <td className="py-3 px-4 text-sm font-medium text-gray-900">
                      {item.orderNumber}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(item.createdDate).toLocaleDateString('es-PE')}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {new Date(item.receivedDate).toLocaleDateString('es-PE')}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {item.supplierName}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {item.destinationName}
                    </td>
                    <td className="py-3 px-4">
                      <div>
                        <div className="font-medium text-gray-900">{item.ingredient.name}</div>
                        <div className="text-xs text-gray-500">{item.ingredient.category} • {item.unit}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">
                      {item.quantityRequested}
                    </td>
                    <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">
                      {item.quantityReceived}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`text-sm font-medium ${item.difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatPercentage(0).slice(0, 1)}{item.difference.toFixed(2)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Badge className={getDifferenceColor(item.differencePercentage)}>
                        {formatPercentage(item.differencePercentage)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-pulse" />
            <p className="text-gray-500">Analizando diferencias...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-orange-500" />
            Reporte de Diferencias
          </h2>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Local</Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Seleccionar local" />
                </SelectTrigger>
                <SelectContent>
                  {locationsWithAll.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-2" />
                        {location.nombre}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Rango</Label>
              <DateRangeQuickFilter
                value={quickRange}
                onChange={setQuickRange}
                reportType="differences"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Desde</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => handleDateChange('dateFrom', e.target.value)}
                className="w-[140px]"
                disabled={quickRange !== 'custom'}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Hasta</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => handleDateChange('dateTo', e.target.value)}
                className="w-[140px]"
                disabled={quickRange !== 'custom'}
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Preparado por</Label>
              <Select value={selectedPreparedBy} onValueChange={setSelectedPreparedBy}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  {preparedByOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id}>{option.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleFiltersChange}
              className="bg-macartur-primary hover:bg-macartur-primary/90"
            >
              <Filter className="h-4 w-4 mr-2" />
              Generar Reporte
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Navigation Tabs */}
      <div className="flex space-x-2 border-b pb-4">
        <Button
          variant={activeTab === 'transfers' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('transfers')}
          className={activeTab === 'transfers' ? 'bg-macartur-primary' : ''}
        >
          <Truck className="w-4 h-4 mr-2" />
          Transferencias
        </Button>
        <Button
          variant={activeTab === 'purchase-orders' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('purchase-orders')}
          className={activeTab === 'purchase-orders' ? 'bg-macartur-primary' : ''}
        >
          <ShoppingCart className="w-4 h-4 mr-2" />
          Órdenes de Compra
        </Button>
      </div>

      {/* Content */}
      {activeTab === 'transfers' ? renderTransfersTab() : renderPurchaseOrdersTab()}
    </div>
  );
};